import { z } from 'zod'
import { instantSchema } from '../validators/temporal.validator.js'
import { roleSchema } from '../enums/role.enum.js'
import { userStatusSchema } from '../enums/user-status.enum.js'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'
import { tenantRoleReachSchema } from '../enums/tenant-role-reach.enum.js'

/**
 * Someone somebody works with and may have invited — an event's client, its
 * planner's contact, a company's owner — as a list row shows them: who they
 * are and where their invitation stands. One shape, so the badge and the Send
 * or Resend button read the same wherever the person appears.
 */
export const invitedContactDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
  status: userStatusSchema,
  /** When they were last invited, or null if they never have been — see `UserDto`. */
  invitedAt: instantSchema.nullable(),
  /** When the outstanding invitation stops working; null when none is. */
  inviteExpiresAt: instantSchema.nullable(),
  /**
   * A planner's invitation that works past its date, because the planner has
   * this event still ahead: it stays open while they have one (see
   * `plannerInvitationStillOpen`). `inviteExpiresAt` is then only the date on
   * the link. Defaulted while an older API is still answering.
   */
  inviteKeptOpen: z.boolean().default(false),
  /** A client's phone, wherever their email is shown; null for anyone else. */
  phone: z.string().nullable().default(null),
})

export type InvitedContactDto = z.infer<typeof invitedContactDtoSchema>

/** The company role a member of staff works under, as a people list names it. */
export const userStaffRoleDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  reach: tenantRoleReachSchema,
})

export type UserStaffRoleDto = z.infer<typeof userStaffRoleDtoSchema>

/**
 * A user as the tenant-admin user manager sees them. No secrets — the password
 * hash and tokens never cross the wire.
 */
export const userDtoSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  role: roleSchema,
  status: userStatusSchema,
  /**
   * When an invitation was last sent, `null` if none ever was, and absent where the
   * question cannot arise.
   *
   * `PENDING` means only "has not signed in", which for a client conflates two
   * different things: someone who was invited and has not got round to it, and
   * someone who exists only as a contact and has never been written to at all. A
   * Prism import creates the second kind by the hundred, and adding a client to an
   * event by hand does too — `ensureClient` is the one path that creates a user
   * without inviting them. The listing called both "Invited", which was untrue of
   * every one of them.
   *
   * Optional rather than required because for every other role it would be noise:
   * org admins, venue admins, vendor users and super admins are only ever created
   * by an invite, and the send failing deletes the account — so their `PENDING`
   * unambiguously means invited, and those services leave this off.
   *
   * Derived from whether an invitation was actually issued, never stored as a flag,
   * so it cannot drift from what happened and is already right for rows created
   * before the distinction existed.
   */
  invitedAt: instantSchema.nullable().optional(),
  /**
   * When the outstanding invitation stops working; null when there is none
   * outstanding — never sent, already accepted, or already expired.
   *
   * Separate from `invitedAt` because they answer different questions.
   * `invitedAt` is "have we ever written to this person", which decides Send
   * versus Resend and stays true forever. This is "can they still get in on it",
   * which stops being true a week later — and without it a lapsed invitation and
   * a live one looked identical, so nobody knew to send another.
   */
  inviteExpiresAt: instantSchema.nullable().optional(),
  /** A client's phone, shown with their email. Absent for other roles. */
  phone: z.string().nullable().optional(),
  /** Browser-usable avatar (signed GET, external, or data URL); null if none. */
  avatarUrl: browserAssetUrlSchema.nullable(),
  /**
   * Venues this user manages as a VENUE_ADMIN (empty for other roles / unassigned).
   * Present on tenant user lists so the UI can filter without N+1 requests.
   */
  managedVenueIds: z.array(z.string().uuid()).default([]),
  /**
   * For staff, the company role they work under; null for everyone else, and
   * where a list doesn't say. Defaulted while an older API is still answering.
   */
  staffRole: userStaffRoleDtoSchema.nullable().default(null),
  createdAt: instantSchema,
})

export type UserDto = z.infer<typeof userDtoSchema>

/**
 * The roles the caller may give people (docs/custom-roles.spec.md, "People and
 * the guardrail"), for the role picker. Every company role is listed, so a
 * person on one the caller can't give still reads by its name; `mayGive` says
 * which can be chosen.
 */
export const assignableRolesDtoSchema = z.object({
  /** Making someone an organization admin, or a client: organization admins only. */
  organizationAdmin: z.boolean(),
  client: z.boolean(),
  roles: z.array(
    userStaffRoleDtoSchema.extend({
      builtIn: z.boolean(),
      /** Every permission at or below the caller's, and no wider reach. */
      mayGive: z.boolean(),
    }),
  ),
})

export type AssignableRolesDto = z.infer<typeof assignableRolesDtoSchema>

export const userListDtoSchema = z.object({
  users: z.array(userDtoSchema),
  total: z.number().int().nonnegative(),
})

export type UserListDto = z.infer<typeof userListDtoSchema>

/** Venues a VENUE_ADMIN is assigned to manage (empty = none yet). */
export const managedVenuesDtoSchema = z.object({
  venueIds: z.array(z.string().uuid()),
})

export type ManagedVenuesDto = z.infer<typeof managedVenuesDtoSchema>
